import { useState, useMemo, lazy, Suspense } from "react";
import { useMutation, useQuery } from "convex/react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Globe,
  Mic,
  Sparkles,
  FileText,
  Activity,
  HardDrive,
  Wrench,
  Check,
  RotateCcw,
  Eye,
  Code,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { api } from "@convex/_generated/api";
import type { Doc, Id } from "@convex/_generated/dataModel";
import {
  badgeClass,
  cardClass,
  errorMessage,
  inputClass,
  monoMetaClass,
  primaryButtonClass,
  secondaryButtonClass,
  selectClass,
} from "@/lib/ui";
import {
  GROQ_ASR_MODELS,
  OPENROUTER_SUMMARY_MODELS,
  RETENTION_PRESETS,
  PROMPT_TEMPLATES,
  DEFAULT_PROMPT_TEXT,
  getAvailableTimezones,
  getUserDeviceTimezone,
} from "@/lib/settingsOptions";

const MarkdownView = lazy(() => import("@/components/MarkdownView"));

type FormState = {
  timezone: string;
  retentionDays: string;
  asrModel: string;
  summaryModel: string;
  summaryPrompt: string;
  autoMatchEnabled: boolean;
  autoSummarize: boolean;
};

export default function Settings() {
  const settings = useQuery(api.settings.get);

  if (settings === undefined) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex items-center gap-2.5 font-mono text-xs text-neutral-500">
          <RefreshCw className="h-4 w-4 animate-spin text-neutral-400" />
          <span>Loading settings…</span>
        </div>
      </div>
    );
  }
  if (settings === null) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="font-mono text-xs text-neutral-500">Initializing settings…</p>
      </div>
    );
  }
  // Remounting on _id resets the form to server state without an effect.
  return <SettingsForm key={settings._id} settings={settings} />;
}

function SettingsForm({ settings }: { settings: Doc<"settings"> }) {
  const update = useMutation(api.settings.update);
  const [form, setForm] = useState<FormState>({
    timezone: settings.timezone,
    retentionDays: String(settings.retentionDays),
    asrModel: settings.asrModel,
    summaryModel: settings.summaryModel,
    summaryPrompt: settings.summaryPrompt,
    autoMatchEnabled: settings.autoMatchEnabled,
    autoSummarize: settings.autoSummarize,
  });

  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [promptViewMode, setPromptViewMode] = useState<"edit" | "preview">("edit");

  // Selection states (handles preset vs custom)
  const availableTimezones = useMemo(() => getAvailableTimezones(), []);
  const userDeviceTz = useMemo(() => getUserDeviceTimezone(), []);

  const isPresetTimezone = availableTimezones.includes(form.timezone);
  const [customTimezoneMode, setCustomTimezoneMode] = useState(!isPresetTimezone);

  const matchedRetention = RETENTION_PRESETS.find(
    (p) => String(p.value) === form.retentionDays,
  );
  const [customRetentionMode, setCustomRetentionMode] = useState(!matchedRetention);

  const matchedAsr = GROQ_ASR_MODELS.find((m) => m.id === form.asrModel);
  const [customAsrMode, setCustomAsrMode] = useState(!matchedAsr);

  const matchedSummary = OPENROUTER_SUMMARY_MODELS.find(
    (m) => m.id === form.summaryModel,
  );
  const [customSummaryMode, setCustomSummaryMode] = useState(!matchedSummary);

  const matchingPromptTemplate = PROMPT_TEMPLATES.find(
    (t) => t.prompt.trim() === form.summaryPrompt.trim(),
  );
  const currentPromptTemplateId = matchingPromptTemplate
    ? matchingPromptTemplate.id
    : "custom";

  // Dirty state tracking
  const isDirty = useMemo(() => {
    return (
      form.timezone !== settings.timezone ||
      form.retentionDays !== String(settings.retentionDays) ||
      form.asrModel !== settings.asrModel ||
      form.summaryModel !== settings.summaryModel ||
      form.summaryPrompt !== settings.summaryPrompt ||
      form.autoMatchEnabled !== settings.autoMatchEnabled ||
      form.autoSummarize !== settings.autoSummarize
    );
  }, [form, settings]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setStatus(null);
  }

  function handleReset() {
    setForm({
      timezone: settings.timezone,
      retentionDays: String(settings.retentionDays),
      asrModel: settings.asrModel,
      summaryModel: settings.summaryModel,
      summaryPrompt: settings.summaryPrompt,
      autoMatchEnabled: settings.autoMatchEnabled,
      autoSummarize: settings.autoSummarize,
    });
    setCustomTimezoneMode(!availableTimezones.includes(settings.timezone));
    setCustomRetentionMode(
      !RETENTION_PRESETS.some((p) => p.value === settings.retentionDays),
    );
    setCustomAsrMode(!GROQ_ASR_MODELS.some((m) => m.id === settings.asrModel));
    setCustomSummaryMode(
      !OPENROUTER_SUMMARY_MODELS.some((m) => m.id === settings.summaryModel),
    );
    setStatus(null);
    setError(null);
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setStatus(null);

    const retentionDays = Number(form.retentionDays);
    if (!Number.isInteger(retentionDays) || retentionDays < 1) {
      setError("Audio retention must be a whole number of days (at least 1).");
      return;
    }
    if (!form.timezone.trim()) {
      setError("Timezone cannot be empty.");
      return;
    }
    if (!form.asrModel.trim()) {
      setError("Groq ASR model slug cannot be empty.");
      return;
    }
    if (!form.summaryModel.trim()) {
      setError("Summary model slug cannot be empty.");
      return;
    }
    if (!form.summaryPrompt.trim()) {
      setError("Summary prompt template cannot be empty.");
      return;
    }

    setSaving(true);
    try {
      await update({
        timezone: form.timezone.trim(),
        retentionDays,
        asrModel: form.asrModel.trim(),
        summaryModel: form.summaryModel.trim(),
        summaryPrompt: form.summaryPrompt,
        autoMatchEnabled: form.autoMatchEnabled,
        autoSummarize: form.autoSummarize,
      });
      setStatus("Settings saved successfully.");
    } catch (caught) {
      setError(errorMessage(caught, "Failed to save settings."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto flex max-w-3xl flex-col gap-8 pb-16">
      {/* Header */}
      <div className="relative pb-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h1 className="text-2xl sm:text-3xl font-light tracking-[-0.05em] text-[#222] dark:text-[#ededeb]">
              Settings
            </h1>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              Configure transcription, AI models, timetable wall-clock zone, and summary templates.
            </p>
          </div>
          <div className="flex items-center gap-2 font-mono text-xs text-neutral-500 dark:text-neutral-400">
            <span className={`${badgeClass} bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300`}>
              Prompt v{settings.promptVersion}
            </span>
            <span className={`${badgeClass} bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300`}>
              Provider: {settings.asrProvider}
            </span>
          </div>
        </div>
        <div className="meniscus absolute bottom-0 left-0 w-full h-[1px] opacity-40 pointer-events-none" />
      </div>

      {/* SECTION 1: General & Regional */}
      <div className={cardClass}>
        <div className="flex items-center gap-2.5 pb-4 border-b border-[#ecece9] dark:border-[#222220]">
          <div className="rounded-lg bg-neutral-100 p-2 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
            <Globe className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-[#222] dark:text-[#ededeb]">
              General & Regional
            </h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Timezone for schedule rules and raw audio retention window.
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-5">
          {/* Timezone Selection */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label htmlFor="settings-timezone" className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
                Timezone
              </label>
              {form.timezone !== userDeviceTz && (
                <button
                  type="button"
                  onClick={() => {
                    set("timezone", userDeviceTz);
                    setCustomTimezoneMode(!availableTimezones.includes(userDeviceTz));
                  }}
                  className="font-mono text-[11px] text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors cursor-pointer"
                >
                  Use detected timezone ({userDeviceTz})
                </button>
              )}
            </div>

            {!customTimezoneMode ? (
              <div className="flex items-center gap-2">
                <select
                  id="settings-timezone"
                  className={`${selectClass} min-w-0 flex-1`}
                  value={form.timezone}
                  onChange={(e) => {
                    if (e.target.value === "__custom__") {
                      setCustomTimezoneMode(true);
                    } else {
                      set("timezone", e.target.value);
                    }
                  }}
                >
                  {availableTimezones.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                  <option value="__custom__">Custom timezone…</option>
                </select>
                <button
                  type="button"
                  onClick={() => setCustomTimezoneMode(true)}
                  className="text-xs text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-300 px-2 py-1"
                >
                  Custom
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  id="settings-timezone"
                  className={`${inputClass} min-w-0 flex-1 font-mono text-xs`}
                  value={form.timezone}
                  onChange={(e) => set("timezone", e.target.value)}
                  placeholder="e.g. Europe/Kyiv"
                />
                <button
                  type="button"
                  onClick={() => {
                    setCustomTimezoneMode(false);
                    if (!availableTimezones.includes(form.timezone)) {
                      set("timezone", availableTimezones[0] || "Europe/Kyiv");
                    }
                  }}
                  className={secondaryButtonClass}
                >
                  List
                </button>
              </div>
            )}
            <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
              Schedule rules are wall-clock in this zone. Changing it materializes and realigns the schedule window.
            </p>
          </div>

          {/* Retention Selection */}
          <div className="flex flex-col gap-1.5 pt-2 border-t border-[#f0f0ee] dark:border-[#1d1d1c]">
            <label htmlFor="settings-retention" className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
              Audio Retention
            </label>

            {!customRetentionMode ? (
              <div className="flex items-center gap-2">
                <select
                  id="settings-retention"
                  className={`${selectClass} min-w-0 flex-1`}
                  value={form.retentionDays}
                  onChange={(e) => {
                    if (e.target.value === "__custom__") {
                      setCustomRetentionMode(true);
                    } else {
                      set("retentionDays", e.target.value);
                    }
                  }}
                >
                  {RETENTION_PRESETS.map((preset) => (
                    <option key={preset.value} value={preset.value}>
                      {preset.label}
                    </option>
                  ))}
                  <option value="__custom__">Custom retention period…</option>
                </select>
                <button
                  type="button"
                  onClick={() => setCustomRetentionMode(true)}
                  className="text-xs text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-300 px-2 py-1"
                >
                  Custom
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="relative min-w-0 flex-1">
                  <input
                    id="settings-retention"
                    type="number"
                    min="1"
                    className={`${inputClass} w-full pr-12 font-mono text-xs`}
                    value={form.retentionDays}
                    onChange={(e) => set("retentionDays", e.target.value)}
                    placeholder="Days"
                  />
                  <span className="absolute right-3 top-2 text-xs text-neutral-400 pointer-events-none">
                    days
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setCustomRetentionMode(false);
                    set("retentionDays", "30");
                  }}
                  className={secondaryButtonClass}
                >
                  Presets
                </button>
              </div>
            )}
            <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
              Raw audio files are automatically purged after this period. Generated transcripts and summaries are preserved forever.
            </p>
          </div>
        </div>
      </div>

      {/* SECTION 2: Speech Recognition (ASR) */}
      <div className={cardClass}>
        <div className="flex items-center justify-between pb-4 border-b border-[#ecece9] dark:border-[#222220]">
          <div className="flex items-center gap-2.5">
            <div className="rounded-lg bg-neutral-100 p-2 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
              <Mic className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-[#222] dark:text-[#ededeb]">
                Speech Recognition (ASR)
              </h2>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Transcription powered by Groq Whisper Cloud.
              </p>
            </div>
          </div>
          <span className="font-mono text-[11px] text-neutral-400 dark:text-neutral-500">
            Groq API
          </span>
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <label htmlFor="settings-asr-model" className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
            Groq Whisper Model
          </label>

          {!customAsrMode ? (
            <div className="flex items-center gap-2">
              <select
                id="settings-asr-model"
                className={`${selectClass} min-w-0 flex-1`}
                value={form.asrModel}
                onChange={(e) => {
                  if (e.target.value === "__custom__") {
                    setCustomAsrMode(true);
                  } else {
                    set("asrModel", e.target.value);
                  }
                }}
              >
                {GROQ_ASR_MODELS.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name} - {model.tag}
                  </option>
                ))}
                <option value="__custom__">Custom Groq model slug…</option>
              </select>
              <button
                type="button"
                onClick={() => setCustomAsrMode(true)}
                className="text-xs text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-300 px-2 py-1"
              >
                Custom
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                id="settings-asr-model"
                className={`${inputClass} min-w-0 flex-1 font-mono text-xs`}
                value={form.asrModel}
                onChange={(e) => set("asrModel", e.target.value)}
                placeholder="e.g. whisper-large-v3-turbo"
              />
              <button
                type="button"
                onClick={() => {
                  setCustomAsrMode(false);
                  set("asrModel", GROQ_ASR_MODELS[0].id);
                }}
                className={secondaryButtonClass}
              >
                Presets
              </button>
            </div>
          )}

          {matchedAsr && (
            <div className="flex items-center gap-2 mt-0.5">
              <span className="font-mono text-[11px] text-neutral-500 dark:text-neutral-400">
                {matchedAsr.description}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* SECTION 3: AI Summarization & Automation */}
      <div className={cardClass}>
        <div className="flex items-center justify-between pb-4 border-b border-[#ecece9] dark:border-[#222220]">
          <div className="flex items-center gap-2.5">
            <div className="rounded-lg bg-neutral-100 p-2 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-[#222] dark:text-[#ededeb]">
                AI Summarization & Automation
              </h2>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Model selection and background pipeline automation.
              </p>
            </div>
          </div>
          <span className="font-mono text-[11px] text-neutral-400 dark:text-neutral-500">
            OpenRouter
          </span>
        </div>

        <div className="mt-4 flex flex-col gap-5">
          {/* Summary Model Selection */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="settings-summary-model" className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
              OpenRouter Summary Model
            </label>

            {!customSummaryMode ? (
              <div className="flex items-center gap-2">
                <select
                  id="settings-summary-model"
                  className={`${selectClass} min-w-0 flex-1`}
                  value={form.summaryModel}
                  onChange={(e) => {
                    if (e.target.value === "__custom__") {
                      setCustomSummaryMode(true);
                    } else {
                      set("summaryModel", e.target.value);
                    }
                  }}
                >
                  {OPENROUTER_SUMMARY_MODELS.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name} {model.tag ? `[${model.tag}]` : ""}
                    </option>
                  ))}
                  <option value="__custom__">Custom OpenRouter slug…</option>
                </select>
                <button
                  type="button"
                  onClick={() => setCustomSummaryMode(true)}
                  className="text-xs text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-300 px-2 py-1"
                >
                  Custom
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  id="settings-summary-model"
                  className={`${inputClass} min-w-0 flex-1 font-mono text-xs`}
                  value={form.summaryModel}
                  onChange={(e) => set("summaryModel", e.target.value)}
                  placeholder="e.g. google/gemini-2.5-flash"
                />
                <button
                  type="button"
                  onClick={() => {
                    setCustomSummaryMode(false);
                    set("summaryModel", OPENROUTER_SUMMARY_MODELS[0].id);
                  }}
                  className={secondaryButtonClass}
                >
                  Presets
                </button>
              </div>
            )}

            {matchedSummary && (
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                {matchedSummary.description}
              </p>
            )}
          </div>

          {/* Automation Toggles */}
          <div className="flex flex-col gap-3 pt-3 border-t border-[#f0f0ee] dark:border-[#1d1d1c]">
            <ToggleSwitch
              checked={form.autoMatchEnabled}
              onChange={(val) => set("autoMatchEnabled", val)}
              label="Auto-match recordings to schedule lessons"
              description="Automatically associates uploaded audio with matching calendar lessons by scheduled start time."
            />

            <ToggleSwitch
              checked={form.autoSummarize}
              onChange={(val) => set("autoSummarize", val)}
              label="Summarize automatically after transcription"
              description="Automatically enqueues an AI summarization job as soon as transcription completes."
            />
          </div>
        </div>
      </div>

      {/* SECTION 4: Summary Prompt Template */}
      <div className={cardClass}>
        <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-[#ecece9] dark:border-[#222220]">
          <div className="flex items-center gap-2.5">
            <div className="rounded-lg bg-neutral-100 p-2 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
              <FileText className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-[#222] dark:text-[#ededeb]">
                Summary Prompt Template
              </h2>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Template instructions guiding the language model on lecture notes structure.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => {
                set("summaryPrompt", DEFAULT_PROMPT_TEXT);
              }}
              className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-200 px-2 py-1 rounded-md transition-colors"
              title="Reset prompt to default university lecture notes template"
            >
              <RotateCcw className="h-3 w-3" />
              <span>Reset default</span>
            </button>

            <div className="flex items-center rounded-lg border border-[#d1d1cf] p-0.5 dark:border-[#2a2a28]">
              <button
                type="button"
                onClick={() => setPromptViewMode("edit")}
                className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                  promptViewMode === "edit"
                    ? "bg-[#222] text-white dark:bg-[#ededeb] dark:text-[#141413]"
                    : "text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100"
                }`}
              >
                <Code className="h-3 w-3" />
                <span>Editor</span>
              </button>
              <button
                type="button"
                onClick={() => setPromptViewMode("preview")}
                className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                  promptViewMode === "preview"
                    ? "bg-[#222] text-white dark:bg-[#ededeb] dark:text-[#141413]"
                    : "text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100"
                }`}
              >
                <Eye className="h-3 w-3" />
                <span>Preview</span>
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-3">
          {/* Preset Template Selector */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <label htmlFor="settings-prompt-template" className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
                Prompt Template Preset
              </label>
              <span className="font-mono text-[11px] text-neutral-400">
                {form.summaryPrompt.length} chars · {form.summaryPrompt.split("\n").length} lines
              </span>
            </div>

            <select
              id="settings-prompt-template"
              className={selectClass}
              value={currentPromptTemplateId}
              onChange={(e) => {
                const selected = PROMPT_TEMPLATES.find((t) => t.id === e.target.value);
                if (selected) {
                  set("summaryPrompt", selected.prompt);
                }
              }}
            >
              {PROMPT_TEMPLATES.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
              {currentPromptTemplateId === "custom" && (
                <option value="custom">Custom / Modified Prompt</option>
              )}
            </select>

            {matchingPromptTemplate && (
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                {matchingPromptTemplate.description}
              </p>
            )}
          </div>

          {/* Prompt Body: Edit or Preview */}
          {promptViewMode === "edit" ? (
            <div className="flex flex-col gap-1.5">
              <textarea
                aria-label="Summary prompt template"
                className={`${inputClass} min-h-72 font-mono text-xs leading-relaxed resize-y`}
                value={form.summaryPrompt}
                onChange={(e) => set("summaryPrompt", e.target.value)}
                placeholder="Enter summary prompt template…"
              />
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                Saving changes to the summary prompt template will automatically increment the prompt version from v{settings.promptVersion} to v{settings.promptVersion + 1}.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-[#e6e6e4] bg-[#fafaf8] p-4 dark:border-[#232322] dark:bg-[#111110] min-h-72 overflow-y-auto max-h-[480px]">
              <Suspense fallback={<p className="font-mono text-xs text-neutral-500">Loading preview…</p>}>
                <MarkdownView markdown={form.summaryPrompt} />
              </Suspense>
            </div>
          )}
        </div>
      </div>

      {/* Error & Feedback messages */}
      <AnimatePresence>
        {error !== null && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 border border-red-200 dark:bg-red-950/50 dark:text-red-300 dark:border-red-900/50"
          >
            <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />
            <span>{error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sticky Bottom Action Bar */}
      <div className="sticky bottom-18 sm:bottom-4 z-20 flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 rounded-xl border border-[#d1d1cf] bg-[#fdfdfc]/95 p-3 sm:p-3.5 shadow-md backdrop-blur-md dark:border-[#2a2a28] dark:bg-[#141413]/95 transition-colors">
        <div className="flex items-center gap-2.5">
          {isDirty ? (
            <span className="flex items-center gap-1.5 font-mono text-xs text-amber-600 dark:text-amber-400">
              <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
              Unsaved changes
            </span>
          ) : (
            <span className="flex items-center gap-1.5 font-mono text-xs text-neutral-500 dark:text-neutral-400">
              <Check className="h-3 w-3 text-emerald-500" />
              Up to date
            </span>
          )}

          <AnimatePresence>
            {status !== null && (
              <motion.span
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -6 }}
                transition={{ duration: 0.15 }}
                className="font-mono text-xs font-medium text-emerald-600 dark:text-emerald-400"
              >
                · {status}
              </motion.span>
            )}
          </AnimatePresence>
        </div>

        <div className="flex items-center gap-2.5">
          {isDirty && (
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.96 }}
              onClick={handleReset}
              className={secondaryButtonClass}
            >
              Discard
            </motion.button>
          )}

          <motion.button
            type="submit"
            disabled={saving || !isDirty}
            whileHover={isDirty ? { scale: 1.02 } : {}}
            whileTap={isDirty ? { scale: 0.96 } : {}}
            className={`${primaryButtonClass} flex items-center gap-1.5`}
          >
            {saving && <RefreshCw className="h-3 w-3 animate-spin" />}
            <span>{saving ? "Saving…" : "Save changes"}</span>
          </motion.button>
        </div>
      </div>

      {/* SECTION 5: Queue & Background Processing */}
      <ProcessingPanel />

      {/* SECTION 6: Storage & Retention */}
      <StoragePanel />

      {/* SECTION 7: System Maintenance */}
      <MaintenancePanel />
    </form>
  );
}

/**
 * The queue, PLAN.md §10. Only pending, leased and failed jobs show: a done job
 * is visible as a transcript, and the list would otherwise grow without bound.
 */
function ProcessingPanel() {
  const jobs = useQuery(api.jobs.listActive, {});
  const retry = useMutation(api.jobs.retry);
  const reprocess = useMutation(api.jobs.reprocess);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(id: string, action: () => Promise<unknown>, fallback: string) {
    setBusyId(id);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(errorMessage(caught, fallback));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between pb-4 border-b border-[#ecece9] dark:border-[#222220]">
        <div className="flex items-center gap-2.5">
          <div className="rounded-lg bg-neutral-100 p-2 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
            <Activity className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-[#222] dark:text-[#ededeb]">
              Active Processing Queue
            </h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Live transcode and transcription tasks in progress or queued.
            </p>
          </div>
        </div>

        {jobs !== undefined && jobs.length > 0 && (
          <span className={`${badgeClass} bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300`}>
            {jobs.length} active {jobs.length === 1 ? "job" : "jobs"}
          </span>
        )}
      </div>

      {jobs === undefined && (
        <div className="flex items-center gap-2 pt-4 font-mono text-xs text-neutral-500">
          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          <span>Checking queue…</span>
        </div>
      )}

      {jobs !== undefined && jobs.length === 0 && (
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <Check className="h-6 w-6 text-emerald-500 opacity-80" />
          <p className="mt-2 text-xs font-medium text-neutral-700 dark:text-neutral-300">
            Queue is idle
          </p>
          <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
            All transcode and transcription jobs are completed.
          </p>
        </div>
      )}

      {jobs !== undefined && jobs.length > 0 && (
        <ul className="mt-4 flex flex-col gap-2.5">
          {jobs.map((job) => (
            <li
              key={job._id}
              className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-[#e6e6e4] bg-[#fafaf8] p-3 dark:border-[#232322] dark:bg-[#111110]"
            >
              <span className={`${badgeClass} ${jobStateClass(job.state)}`}>
                {job.state}
              </span>
              <span className="text-xs font-medium text-[#222] dark:text-[#ededeb]">
                {job.kind}
              </span>
              <span className={monoMetaClass}>
                {job.recordingStartedAt === undefined
                  ? "no recording"
                  : new Date(job.recordingStartedAt).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
              </span>
              <span className={monoMetaClass}>
                attempt {job.attempts}/{job.maxAttempts}
                {job.state === "pending" && job.dueInMs > 0
                  ? ` · retry in ${Math.ceil(job.dueInMs / 60_000)} min`
                  : ""}
              </span>

              <span className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  disabled={busyId !== null || job.state === "leased"}
                  onClick={() =>
                    void run(job._id, () => retry({ jobId: job._id }), "Retry failed.")
                  }
                  className={secondaryButtonClass}
                >
                  Retry
                </button>
                {job.recordingId !== undefined && (
                  <button
                    type="button"
                    disabled={busyId !== null || job.state === "leased"}
                    onClick={() =>
                      void run(
                        job._id,
                        () =>
                          reprocess({
                            recordingId: job.recordingId as Id<"recordings">,
                          }),
                        "Reprocess failed.",
                      )
                    }
                    className={secondaryButtonClass}
                  >
                    Reprocess
                  </button>
                )}
              </span>

              {job.error !== undefined && (
                <p className="w-full font-mono text-[11px] break-words text-red-600 dark:text-red-400 pt-1 border-t border-red-100 dark:border-red-950">
                  {job.error}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {error !== null && (
        <p className="mt-3 font-mono text-xs text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}

function jobStateClass(state: string): string {
  if (state === "leased") {
    return "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300";
  }
  if (state === "failed") {
    return "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300";
  }
  return "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300";
}

/**
 * The audio volume, PLAN.md §9. Convex knows what it is keeping and what has
 * already been forgotten; the disk numbers come from the worker, which is the
 * only thing in the system that can see the filesystem - so they carry the age
 * of the reading rather than pretending to be live.
 */
function StoragePanel() {
  const status = useQuery(api.retention.status, {});
  const sweep = useMutation(api.retention.sweepNow);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  async function handleSweep() {
    setBusy(true);
    setNote(null);
    try {
      await sweep({});
      setNote("Sweep queued - the worker picks it up within a few seconds.");
    } catch (caught) {
      setNote(errorMessage(caught, "Could not queue the sweep."));
    } finally {
      setBusy(false);
    }
  }

  const disk = status?.disk ?? null;
  const usedBytes =
    disk === null ? 0 : Math.max(0, disk.diskTotalBytes - disk.diskFreeBytes);
  const usedPercent =
    disk === null || disk.diskTotalBytes === 0
      ? 0
      : Math.min(100, (usedBytes / disk.diskTotalBytes) * 100);
  // The worker refuses to start a transcode below 2 GB free (PLAN.md §15), so
  // that is the threshold worth colouring, not an arbitrary percentage.
  const freeGb = disk === null ? 0 : disk.diskFreeBytes / 1024 ** 3;
  const barClass =
    disk === null || freeGb >= 2
      ? "bg-emerald-500"
      : freeGb >= 1
        ? "bg-amber-500"
        : "bg-red-500";

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between gap-3 border-b border-[#ecece9] pb-4 dark:border-[#222220]">
        <div className="flex items-center gap-2.5">
          <div className="rounded-lg bg-neutral-100 p-2 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
            <HardDrive className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-[#222] dark:text-[#ededeb]">
              Storage &amp; Retention
            </h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Captures are deleted{" "}
              {status === undefined
                ? "after the retention window"
                : `after ${status.retentionDays} days`}{" "}
              once they have a transcript. Notes and transcripts are kept forever.
            </p>
          </div>
        </div>
        <motion.button
          type="button"
          disabled={busy || status === undefined}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.96 }}
          onClick={() => void handleSweep()}
          className={`${secondaryButtonClass} flex shrink-0 items-center gap-1.5`}
        >
          {busy && <RefreshCw className="h-3 w-3 animate-spin text-neutral-400" />}
          <span>{busy ? "Queueing…" : "Run sweep now"}</span>
        </motion.button>
      </div>

      {status === undefined ? (
        <div className="flex items-center gap-2 pt-4 font-mono text-xs text-neutral-500">
          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          <span>Reading storage…</span>
        </div>
      ) : (
        <>
          <div className="mt-4">
            {disk === null ? (
              <p className="font-mono text-xs text-neutral-500 dark:text-neutral-400">
                The worker has not reported yet - disk usage appears once it has
                completed one poll.
              </p>
            ) : (
              <>
                <div className="flex items-baseline justify-between">
                  <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
                    Audio volume
                  </span>
                  <span className={monoMetaClass}>
                    {formatBytes(disk.diskFreeBytes)} free of{" "}
                    {formatBytes(disk.diskTotalBytes)}
                  </span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800">
                  <motion.div
                    className={`h-full rounded-full ${barClass}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${usedPercent}%` }}
                    transition={{ type: "spring", stiffness: 120, damping: 20 }}
                  />
                </div>
                <p className={`mt-2 ${monoMetaClass}`}>
                  {formatBytes(disk.audioBytes)} in {disk.recordingDirs} recording
                  {disk.recordingDirs === 1 ? "" : "s"} · {disk.audioFileCount} file
                  {disk.audioFileCount === 1 ? "" : "s"} · read{" "}
                  {relativeTime(disk.updatedAt)}
                </p>
              </>
            )}
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat
              label="Audio retained"
              value={String(status.retained)}
              sub={formatBytes(status.retainedBytes)}
            />
            <Stat
              label="Due next sweep"
              value={String(status.dueNow)}
              sub={status.dueNow > 0 ? "past retention" : "nothing expired"}
            />
            <Stat
              label="Already freed"
              value={String(status.deleted)}
              sub="transcripts kept"
            />
            <Stat
              label="Oldest capture"
              value={
                status.oldestRetainedAt === undefined
                  ? "-"
                  : new Date(status.oldestRetainedAt).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })
              }
              sub={
                status.oldestRetainedAt === undefined
                  ? "no audio held"
                  : relativeTime(status.oldestRetainedAt)
              }
            />
          </dl>

          <p className={`mt-4 ${monoMetaClass}`}>
            {disk === null || disk.lastSweepAt === undefined
              ? "No sweep has run yet. The cron runs daily at 03:40 UTC."
              : `Last sweep ${relativeTime(disk.lastSweepAt)}: ` +
                `${disk.lastSweepDeleted ?? 0} recording(s), ` +
                `${disk.lastSweepOrphans ?? 0} orphan(s), ` +
                `${formatBytes(disk.lastSweepBytesFreed ?? 0)} freed.`}
          </p>

          {note !== null && (
            <p className="mt-2 font-mono text-xs text-neutral-600 dark:text-neutral-300">
              {note}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded-lg border border-[#e6e6e4] bg-[#fafaf8] p-3 dark:border-[#232322] dark:bg-[#111110]">
      <dt className="text-[10px] font-medium tracking-wide text-neutral-500 uppercase dark:text-neutral-400">
        {label}
      </dt>
      <dd className="mt-1 text-sm font-semibold text-[#222] dark:text-[#ededeb]">
        {value}
      </dd>
      <dd className={monoMetaClass}>{sub}</dd>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

/** Coarse on purpose: this is the age of a reading, not a clock. */
function relativeTime(timestamp: number): string {
  const seconds = Math.round((Date.now() - timestamp) / 1_000);
  if (seconds < 90) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

/**
 * Every schedule edit already regenerates the lessons it touched and a daily
 * cron rolls the window forward, so this is a repair tool, not part of the
 * normal flow.
 */
function MaintenancePanel() {
  const rebuild = useMutation(api.lessons.rebuild);
  const [state, setState] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleRebuild() {
    setBusy(true);
    setState(null);
    try {
      const count = await rebuild({});
      setState(`Rebuilt successfully: ${count} lessons active in timetable window.`);
    } catch (caught) {
      setState(errorMessage(caught, "Rebuild failed."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-2.5 pb-4 border-b border-[#ecece9] dark:border-[#222220]">
        <div className="rounded-lg bg-neutral-100 p-2 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
          <Wrench className="h-4 w-4" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-[#222] dark:text-[#ededeb]">
            Schedule Maintenance
          </h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Regenerate materialized lessons from recurring timetable rules (−60 to +120 days).
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <motion.button
          type="button"
          disabled={busy}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.96 }}
          onClick={() => void handleRebuild()}
          className={`${secondaryButtonClass} flex items-center gap-1.5`}
        >
          {busy && <RefreshCw className="h-3 w-3 animate-spin text-neutral-400" />}
          <span>{busy ? "Rebuilding lessons…" : "Rebuild lesson window"}</span>
        </motion.button>
        {state !== null && (
          <span className="font-mono text-xs text-neutral-600 dark:text-neutral-300">
            {state}
          </span>
        )}
      </div>
    </div>
  );
}

function ToggleSwitch({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onChange(!checked)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onChange(!checked);
        }
      }}
      className="flex items-start justify-between gap-4 p-2.5 -mx-2.5 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-900/50 transition-colors cursor-pointer select-none"
    >
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-medium text-neutral-800 dark:text-neutral-200">
          {label}
        </span>
        {description && (
          <span className="text-[11px] text-neutral-500 dark:text-neutral-400">
            {description}
          </span>
        )}
      </div>

      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={(e) => {
          e.stopPropagation();
          onChange(!checked);
        }}
        className={[
          "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden",
          checked ? "bg-neutral-900 dark:bg-white" : "bg-neutral-300 dark:bg-neutral-700",
        ].join(" ")}
      >
        <motion.span
          layout
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
          className={[
            "pointer-events-none inline-block h-4 w-4 rounded-full shadow-sm ring-0",
            checked ? "bg-white dark:bg-neutral-900 ml-4" : "bg-white dark:bg-neutral-300 ml-0",
          ].join(" ")}
        />
      </button>
    </div>
  );
}
